import { GraduationCap, Users, MapPin, Award, Clock, Gift, Phone, Star, Home, ShieldCheck } from 'lucide-react';

export const HERO_BULLETS = [
  {
    icon: GraduationCap,
    title: 'Serving grades 2\u2009\u2013\u200912',
    subtitle: 'Elementary through high school, curriculum tuned to each student.',
    badge: '2 \u2013 12',
  },
  {
    icon: Users,
    title: 'Only 4 students per class',
    subtitle: 'Tight cohort, real attention, every question answered.',
    badge: '4 max',
  },
  {
    icon: MapPin,
    title: 'Irving, TX 75063 \u2014 near Las Colinas',
    subtitle: 'A quiet, focused classroom space.',
    badge: 'In person',
  },
  {
    icon: Award,
    title: '15+ years teaching math',
    subtitle: 'Led by Abraham \u2014 a classroom veteran, not a side-gig tutor.',
    badge: '15+ yrs',
  },
  {
    icon: Clock,
    title: 'Twice a week \u00B7 2 hours each',
    subtitle: 'In-person only \u2014 4 focused hours every week for 10 weeks.',
    badge: '4 hrs/wk',
  },
  {
    icon: Gift,
    title: 'First 2 weeks free',
    subtitle: "No card on file \u2014 only pay if you\u2019re satisfied after the trial.",
    badge: '$0 trial',
    badgeVariant: 'green',
  },
  {
    icon: Phone,
    title: 'Text or call Abraham',
    subtitle: 'Questions before you sign up? Reach out any time.',
    badge: '(949) 300-2265',
  },
];

export const WHY_THIS_EXISTS_CARDS = [
  {
    number: '01',
    label: 'The drift',
    title: '10+ weeks of zero practice',
    description:
      'Summer is the longest stretch of the year without math. Skills that took 9 months to build can fade in a few weeks of nothing.',
  },
  {
    number: '02',
    label: 'The sprint',
    title: 'September moves fast',
    description:
      "The first six weeks of a new grade introduce most of the year\u2019s new ideas. Miss the foundation and the whole year feels like catch-up.",
  },
  {
    number: '03',
    label: 'The wall',
    title: "By October it\u2019s hard to fix",
    description:
      'Once a child decides "I\u2019m bad at math," tutoring becomes about confidence, not just content. Starting ahead is cheaper than rebuilding.',
  },
];

export const HOW_IT_WORKS_STEPS = [
  {
    number: '01',
    title: 'Reserve a seat',
    description:
      "Pick your child\u2019s math level and a class time. Takes about a minute. We hold the seat \u2014 no payment, no commitment.",
    badge: '~ 1 minute',
  },
  {
    number: '02',
    title: 'Attend 2 free weeks',
    description:
      "Four full sessions, eight hours of instruction. Same teacher, same group, same room you\u2019d be in all summer. If it\u2019s not the right fit, walk away.",
    badge: '8 hours \u00B7 $0',
  },
  {
    number: '03',
    title: 'Continue if it works',
    description:
      'If your child wants to come back after week two, you pay $25/hour for the remaining 8 weeks.',
    badge: '$25/hr \u00B7 cancel anytime',
  },
];

export const TEACHER_STATS = [
  { icon: GraduationCap, text: '15+ years teaching math' },
  { icon: Star, text: '200+ students taught' },
  { icon: Home, text: 'Small-group & private tutoring' },
  { icon: ShieldCheck, text: 'Background-checked & insured' },
];

export const TESTIMONIALS = [
  {
    quote: "My son\u2019s Algebra grade went from C to A!",
    name: 'Sana',
    initials: 'SA',
    subtitle: 'Parent of 6th grader',
    stars: 5,
  },
  {
    quote: "We tried two tutoring chains before this. The difference is that Abraham actually teaches \u2014 he doesn\u2019t just sit next to kids while they do worksheets.",
    name: 'Mike & Olivia C.',
    initials: 'MO',
    subtitle: 'Parents of 4th & 9th graders',
    stars: 5,
  },
  {
    quote: "Four students per class is the real deal. My son got called on every five minutes. There\u2019s nowhere to hide, and that\u2019s exactly what he needed.",
    name: 'Daniel J.',
    initials: 'DJ',
    subtitle: 'Parent of 10th grader \u00B7 Las Colinas',
    stars: 5,
  },
];

export const FAQ_ITEMS = [
  {
    question: 'How does the 2-week free trial actually work?',
    answer: "You sign up, attend four full classes over two weeks, and decide afterward. No payment info up front. If your child isn\u2019t enjoying it or you don\u2019t see progress, you owe nothing and we part friends.",
  },
  {
    question: 'What does it cost after the trial?',
    answer: '$25/hour for the remaining 8 weeks \u2014 about $400 total for the rest of the summer (16 sessions, 32 hours of small-group instruction). You can pause or cancel weekly, no contract.',
  },
  {
    question: 'Where exactly do classes meet?',
    answer: 'A quiet, focused classroom space in Irving, TX 75063 \u2014 minutes from Las Colinas, Valley Ranch, and Coppell. We send the exact address after you reserve a seat.',
  },
  {
    question: 'What if my child is way ahead \u2014 or way behind?',
    answer: "Classes are grouped by level, not just grade. Before week one, Abraham does a short diagnostic and places your child where the work will be challenging but reachable. We\u2019ve placed 4th graders in pre-algebra and 9th graders in foundations \u2014 whatever fits.",
  },
  {
    question: 'Are classes online or in person?',
    answer: "In person only. The small-group dynamic is the whole product \u2014 it doesn\u2019t translate to Zoom. If you\u2019re farther than ~25 minutes from Irving, this probably isn\u2019t the right fit.",
  },
  {
    question: 'How many seats are left?',
    answer: 'We cap every class at 4 students and we don\u2019t add a 5th. Enrollment for Summer 2026 closes May 24. After that the cohort is locked until next year.',
    highlightText: 'May 24',
  },
];